import type { ComponentProps, ReactNode } from "react";
import { useId } from "react";

import {
  Badge,
  Box,
  Button,
  Callout,
  Card,
  DataList,
  Dialog,
  Flex,
  Heading,
  ScrollArea,
  Select,
  Separator,
  Spinner,
  Text,
  TextField,
} from "@radix-ui/themes";

import type { Order } from "~/v2/api/rest/account";
import { Field } from "~/v3-poc/components/Field";
import type { OrderReplacementController } from "./OrderReplacementDialog.controller";
import { formatOrderLabel, formatOrderQuantity } from "./OrdersWidget.utils";

export interface OrderReplacementDialogPresentationProps {
  open: boolean;
  order?: Order | null;
  controller?: OrderReplacementController;
  eligibilityDisabledReason?: string;
  isLoading: boolean;
  isFetching: boolean;
  isError: boolean;
  isNotFound: boolean;
  isPending: boolean;
  errorMessage?: string;
  submissionError?: string;
  onOpenChange: (open: boolean) => void;
  onCloseAutoFocus?: ComponentProps<typeof Dialog.Content>["onCloseAutoFocus"];
  onRetry: () => void;
}

const LoadingState = () => (
  <Flex align="center" justify="center" gap="2" py="6" role="status">
    <Spinner />
    <Text size="2" color="gray">
      Loading current order…
    </Text>
  </Flex>
);

const RequestErrorState = ({
  isNotFound,
  isFetching,
  errorMessage,
  onRetry,
}: Pick<
  OrderReplacementDialogPresentationProps,
  "isNotFound" | "isFetching" | "errorMessage" | "onRetry"
>) => (
  <Callout.Root color={isNotFound ? "orange" : "red"} role="alert">
    <Callout.Text>
      {isNotFound
        ? "This order could not be found for the selected account."
        : (errorMessage ?? "Current order details could not be loaded.")}
    </Callout.Text>
    <Button
      type="button"
      size="1"
      variant="soft"
      color={isNotFound ? "orange" : "red"}
      onClick={onRetry}
      loading={isFetching}
    >
      Retry
    </Button>
  </Callout.Root>
);

const OrderContext = ({ order }: { order: Order }) => (
  <Card size="1" variant="surface">
    <DataList.Root size="2">
      <DataList.Item>
        <DataList.Label color="gray">Symbol</DataList.Label>
        <DataList.Value>
          <Text weight="medium">{order.symbol}</Text>
        </DataList.Value>
      </DataList.Item>
      <DataList.Item>
        <DataList.Label color="gray">Side</DataList.Label>
        <DataList.Value>
          <Badge color={order.side === "buy" ? "green" : "red"} variant="soft">
            {formatOrderLabel(order.side)}
          </Badge>
        </DataList.Value>
      </DataList.Item>
      <DataList.Item>
        <DataList.Label color="gray">Type</DataList.Label>
        <DataList.Value>
          <Text>{formatOrderLabel(order.order_type || order.type)}</Text>
        </DataList.Value>
      </DataList.Item>
      <DataList.Item>
        <DataList.Label color="gray">Status</DataList.Label>
        <DataList.Value>
          <Text>{formatOrderLabel(order.status)}</Text>
        </DataList.Value>
      </DataList.Item>
      <DataList.Item>
        <DataList.Label color="gray">Filled quantity</DataList.Label>
        <DataList.Value>
          <Text className="atd-numeric">
            {formatOrderQuantity(order.filled_qty)}
          </Text>
        </DataList.Value>
      </DataList.Item>
    </DataList.Root>
  </Card>
);

const EditStep = ({
  controller,
  formId,
}: {
  controller: OrderReplacementController;
  formId: string;
}) => (
  <form
    id={formId}
    onSubmit={(event) => {
      event.preventDefault();
      controller.onReview();
    }}
  >
    <Flex direction="column" gap="4">
      <Heading as="h3" size="3">
        Editable values
      </Heading>
      <Field label="Quantity" required error={controller.errors.qty}>
        {(control) => (
          <TextField.Root
            {...control}
            value={controller.values.qty}
            inputMode="decimal"
            autoComplete="off"
            onChange={(event) =>
              controller.onFieldChange("qty", event.currentTarget.value)
            }
          />
        )}
      </Field>
      {controller.applicablePriceFields.includes("limitPrice") ? (
        <Field
          label="Limit price"
          required
          error={controller.errors.limitPrice}
        >
          {(control) => (
            <TextField.Root
              {...control}
              value={controller.values.limitPrice}
              inputMode="decimal"
              autoComplete="off"
              onChange={(event) =>
                controller.onFieldChange(
                  "limitPrice",
                  event.currentTarget.value,
                )
              }
            />
          )}
        </Field>
      ) : null}
      {controller.applicablePriceFields.includes("stopPrice") ? (
        <Field label="Stop price" required error={controller.errors.stopPrice}>
          {(control) => (
            <TextField.Root
              {...control}
              value={controller.values.stopPrice}
              inputMode="decimal"
              autoComplete="off"
              onChange={(event) =>
                controller.onFieldChange("stopPrice", event.currentTarget.value)
              }
            />
          )}
        </Field>
      ) : null}
      <Field
        label="Time in force"
        required
        error={controller.errors.timeInForce}
      >
        {(control) => (
          <Select.Root
            value={controller.values.timeInForce}
            onValueChange={(value) =>
              controller.onFieldChange("timeInForce", value)
            }
          >
            <Select.Trigger {...control} aria-label="Time in force" />
            <Select.Content>
              {controller.timeInForceOptions.map((option) => (
                <Select.Item key={option.value} value={option.value}>
                  {option.label}
                </Select.Item>
              ))}
            </Select.Content>
          </Select.Root>
        )}
      </Field>
      {controller.errors.form ? (
        <Callout.Root color="orange" role="alert">
          <Callout.Text>{controller.errors.form}</Callout.Text>
        </Callout.Root>
      ) : null}
    </Flex>
  </form>
);

const ReviewStep = ({
  controller,
  submissionError,
}: {
  controller: OrderReplacementController;
  submissionError?: string;
}) => (
  <Flex direction="column" gap="4">
    <Heading as="h3" size="3">
      Review changes
    </Heading>
    <DataList.Root size="2">
      {controller.changes.map((change) => (
        <DataList.Item key={change.field} align="center">
          <DataList.Label color="gray" minWidth="120px">
            {change.label}
          </DataList.Label>
          <DataList.Value>
            <Flex align="center" gap="2" wrap="wrap">
              <Text color="gray" className="atd-numeric">
                {change.before}
              </Text>
              <Text aria-hidden>→</Text>
              <Text weight="medium" className="atd-numeric">
                {change.after}
              </Text>
            </Flex>
          </DataList.Value>
        </DataList.Item>
      ))}
    </DataList.Root>
    <Text size="2" color="gray">
      The replacement is submitted to the venue; acceptance may happen later.
    </Text>
    {submissionError ? (
      <Callout.Root color="red" role="alert">
        <Callout.Text>{submissionError}</Callout.Text>
      </Callout.Root>
    ) : null}
  </Flex>
);

export const OrderReplacementDialogPresentation = ({
  open,
  order,
  controller,
  eligibilityDisabledReason,
  isLoading,
  isFetching,
  isError,
  isNotFound,
  isPending,
  errorMessage,
  submissionError,
  onOpenChange,
  onCloseAutoFocus,
  onRetry,
}: OrderReplacementDialogPresentationProps) => {
  const formId = useId();
  const symbol = order?.symbol?.trim();
  const title = symbol ? `Edit ${symbol} order` : "Edit order";
  const canRenderForm =
    order && controller && !eligibilityDisabledReason && !isLoading && !isError;

  return (
    <Dialog.Root open={open} onOpenChange={onOpenChange}>
      <Dialog.Content
        maxWidth="680px"
        maxHeight="85vh"
        data-testid="order-replacement-dialog"
        onCloseAutoFocus={onCloseAutoFocus}
        onEscapeKeyDown={(event) => {
          if (isPending) {
            event.preventDefault();
          }
        }}
        onPointerDownOutside={(event) => {
          if (isPending) {
            event.preventDefault();
          }
        }}
      >
        <Flex
          direction="column"
          height="100%"
          overflow="hidden"
          data-testid="order-replacement-frame"
        >
          <Box flexShrink="0">
            <Dialog.Title>{title}</Dialog.Title>
            <Dialog.Description size="2" color="gray">
              Change only quantity and the prices applicable to this order.
            </Dialog.Description>
            {order ? (
              <Box mt="3">
                <OrderContext order={order} />
              </Box>
            ) : null}
          </Box>

          <Box
            data-testid="order-replacement-body"
            my="4"
            minHeight="0"
            flexGrow="1"
            overflow="hidden"
          >
            <ScrollArea
              type="auto"
              scrollbars="vertical"
              data-testid="order-replacement-scroll-viewport"
              className="h-full"
            >
              <Box pr="3">
                {isLoading ? (
                  <LoadingState />
                ) : isError ? (
                  <RequestErrorState
                    isNotFound={isNotFound}
                    isFetching={isFetching}
                    errorMessage={errorMessage}
                    onRetry={onRetry}
                  />
                ) : eligibilityDisabledReason ? (
                  <Callout.Root color="orange" role="alert">
                    <Callout.Text>{eligibilityDisabledReason}</Callout.Text>
                  </Callout.Root>
                ) : canRenderForm ? (
                  controller.step === "edit" ? (
                    <EditStep controller={controller} formId={formId} />
                  ) : (
                    <ReviewStep
                      controller={controller}
                      submissionError={submissionError}
                    />
                  )
                ) : (
                  <Callout.Root color="gray" role="status">
                    <Callout.Text>
                      No current order detail was returned.
                    </Callout.Text>
                  </Callout.Root>
                )}
              </Box>
            </ScrollArea>
          </Box>

          <Box data-testid="order-replacement-footer" flexShrink="0">
            <Separator size="4" mb="3" />
            <Flex align="center" justify="between" gap="3" wrap="wrap">
              <Dialog.Close>
                <Button
                  type="button"
                  variant="soft"
                  color="gray"
                  disabled={isPending}
                >
                  Close
                </Button>
              </Dialog.Close>
              {canRenderForm ? (
                controller.step === "edit" ? (
                  <Button type="submit" form={formId}>
                    Review changes
                  </Button>
                ) : (
                  <Flex align="center" gap="2">
                    <Button
                      type="button"
                      variant="soft"
                      color="gray"
                      disabled={isPending}
                      onClick={controller.onBack}
                    >
                      Back
                    </Button>
                    <Button
                      type="button"
                      disabled={isPending}
                      loading={isPending}
                      onClick={controller.onConfirm}
                    >
                      Submit replacement
                    </Button>
                  </Flex>
                )
              ) : null}
            </Flex>
          </Box>
        </Flex>
      </Dialog.Content>
    </Dialog.Root>
  );
};

export interface OrderReplacementVariantPresentationProps {
  variant: "dialog" | "inline";
  open: boolean;
  dialog: ReactNode;
  inline: ReactNode;
}

export const OrderReplacementVariantPresentation = ({
  variant,
  open,
  dialog,
  inline,
}: OrderReplacementVariantPresentationProps) => {
  if (variant === "dialog") {
    return dialog;
  }
  if (open) {
    return inline;
  }
  return null;
};
